import { IBetItemData, IBetSlipError, factorial, sumOfOddsSubsets } from "@finbackoffice/fe-core";
import {
    Dispatch,
    FC,
    PropsWithChildren,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { TicketType, OddsChangeMode } from "@finbackoffice/enums";
import { BrowserStorageIdsEnum, useDidUpdateEffect } from "@finbackoffice/site-core";
import { ConfigContextV2 } from "./config-v2.provider";

const convertToLocalStorageItems = (bets: IBetItemData[]) => {
    return bets.map((bet) => ({
        ...bet,
        limits: undefined,
        stake: "",
        error: undefined,
    }));
};

const getBetType = (betsAmount: number, betType: TicketType): TicketType => {
    if (betType === TicketType.Single && betsAmount >= 2) {
        return TicketType.Express;
    }

    if (betType === TicketType.Express && betsAmount < 2) {
        return TicketType.Single;
    }

    return betType;
};

interface IBetSlipActionsContext {
    setBetType: (val: TicketType) => void;
    setBetMode: Dispatch<SetStateAction<OddsChangeMode>>;
    addBetItem: (payload: IBetItemData) => void;
    removeBetItem: (id: number) => void;
    updateBetItem: (payload: Partial<IBetItemData>) => void;
    removeAllBetItems: () => void;
    updateButtons: (payload: Record<"placeBetButtonEnabled" | "showAcceptButton", boolean>) => void;
    changeMultipleBetStake: (val: string) => void;
    changeSystemBetStake: (val: string) => void;
    changeSystemBetSelection: (val: number) => void;
    changeBetProcessing: Dispatch<SetStateAction<boolean>>;
    setBetError: Dispatch<SetStateAction<IBetSlipError | null>>;
    isOutcomeSelected: (id: number) => boolean;
}

interface IBetSlipSelectionContext {
    isOutcomeSelected: (id: number) => boolean;
}

export const BetSlipSelectionContext = createContext<IBetSlipSelectionContext>(null as any);

interface IBetSlipItemsContext {
    betItems: IBetItemData[];
}

export const BetSlipItemsContext = createContext<IBetSlipItemsContext>(null as any);

interface IBetSlipContextV2 {
    betType: TicketType;
    placeBetButtonEnabled: boolean;
    betMode: OddsChangeMode;
    multipleBetStake: string;
    systemBetStake: string;
    showAcceptButton: boolean;
    betProcessing: boolean;
    betSlipError: IBetSlipError | null;
    systemBetSelection: number;
    multipleBetTotalOdds: number;
    systemBetVariantsCount: number;
    systemBetTotalOdds: number;
    totalStake: number;
    possibleWinAmount: number;
    getTaxAmount: (possibleBonusAmount?: number) => number;
    getTotalWinAmount: (possibleBonusAmount?: number) => number;
}

export const BetSlipContextV2 = createContext<IBetSlipContextV2>(null as any);
export const BetSlipActionsContext = createContext<IBetSlipActionsContext>(null as any);

export const BetSlipProviderV2: FC<PropsWithChildren> = ({ children }) => {
    const { betProcessorTax } = useContext(ConfigContextV2);

    const [betType, setBetTypeState] = useState(TicketType.Single);
    const [betMode, setBetMode] = useState(OddsChangeMode.Exact);

    const [placeBetButtonEnabled, setPlaceBetButtonEnabled] = useState(false);
    const [showAcceptButton, setShowAcceptButton] = useState(false);

    const [multipleBetStake, setMultipleBetStake] = useState("");
    const [systemBetStake, setSystemBetStake] = useState("");

    const [systemBetSelection, setSystemBetSelection] = useState(2);

    const [betProcessing, setBetProcessing] = useState(false);
    const [error, setError] = useState<IBetSlipError | null>(null);

    const [betItems, setBetItems] = useState<IBetItemData[]>([]);
    const [selectedOutcomeIds, setSelectedOutcomeIds] = useState<Set<number>>(new Set());

    const betItemsRef = useRef(betItems);
    betItemsRef.current = betItems;
    const betProcessingRef = useRef(betProcessing);
    betProcessingRef.current = betProcessing;
    const betTypeRef = useRef(betType);
    betTypeRef.current = betType;

    const oddsSignature = betItems.map((b) => b.outcomeValue).join(",");
    const stakesSignature = betItems.map((b) => b.stake ?? "").join(",");

    const setBetType = useCallback((val: TicketType) => {
        if (!betProcessingRef.current) {
            setBetTypeState(val);
        }
    }, []);

    useEffect(() => {
        if (betItems.length) {
            setBetType(getBetType(betItems.length, betTypeRef.current));
        } else {
            setMultipleBetStake("0");
            setSystemBetStake("0");
        }
    }, [betItems.length, setBetType]);

    useDidUpdateEffect(() => {
        sessionStorage.setItem(
            BrowserStorageIdsEnum.BET_ITEMS,
            JSON.stringify(convertToLocalStorageItems(betItems)),
        );
    }, [betItems.length]);

    const updateBetItemByIndex = useCallback((index: number, payload: IBetItemData) => {
        const newBetItems = [...betItemsRef.current];
        newBetItems[index] = payload;
        setBetItems(newBetItems);
        sessionStorage.setItem(
            BrowserStorageIdsEnum.BET_ITEMS,
            JSON.stringify(convertToLocalStorageItems(newBetItems)),
        );
    }, []);

    const addBetItem = useCallback(
        (payload: IBetItemData) => {
            if (!betProcessingRef.current) {
                const index = betItemsRef.current.findIndex((bet) => bet.gameId === payload.gameId);
                if (index !== -1) {
                    const oldOutcomeId = betItemsRef.current[index].outcomeId;
                    updateBetItemByIndex(index, payload);
                    if (oldOutcomeId !== payload.outcomeId) {
                        setSelectedOutcomeIds((prev) => {
                            const next = new Set(prev);
                            next.delete(oldOutcomeId);
                            next.add(payload.outcomeId);
                            return next;
                        });
                    }
                } else {
                    setBetItems((state) => [...state, payload]);
                    setSelectedOutcomeIds((prev) => new Set(prev).add(payload.outcomeId));
                    setError(null);
                }
            }
        },
        [updateBetItemByIndex],
    );

    const removeBetItem = useCallback((id: number) => {
        if (!betProcessingRef.current) {
            const index = betItemsRef.current.findIndex((item) => item.outcomeId === id);
            if (index === -1 || betItemsRef.current[index].loading) return;
            setBetItems((state) => state.filter((_, i) => i !== index));
            setSelectedOutcomeIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            setError(null);
        }
    }, []);

    const updateBetItem = useCallback((payload: Partial<IBetItemData>) => {
        if (payload.outcomeId) {
            setBetItems((state) => {
                const index = state.findIndex((item) => item.outcomeId === payload.outcomeId);

                if (index !== -1) {
                    const newBetItems = [...state];
                    newBetItems[index] = {
                        ...state[index],
                        ...payload,
                    };
                    return newBetItems;
                }
                return state;
            });
        }
    }, []);

    const removeAllBetItems = useCallback(() => {
        if (!betProcessingRef.current) {
            sessionStorage.removeItem(BrowserStorageIdsEnum.BET_ITEMS);
            setBetItems([]);
            setSelectedOutcomeIds(new Set());
        }
    }, []);

    const updateButtons = useCallback(
        (payload: Record<"placeBetButtonEnabled" | "showAcceptButton", boolean>) => {
            setPlaceBetButtonEnabled(payload.placeBetButtonEnabled);
            setShowAcceptButton(payload.showAcceptButton);
        },
        [],
    );

    const changeMultipleBetStake = useCallback((val: string) => {
        if (!betProcessingRef.current) {
            setMultipleBetStake(val);
        }
    }, []);

    const changeSystemBetStake = useCallback((val: string) => {
        if (!betProcessingRef.current) {
            setSystemBetStake(val);
        }
    }, []);

    const changeSystemBetSelection = useCallback((val: number) => {
        if (!betProcessingRef.current) {
            setSystemBetSelection(val);
        }
    }, []);

    const isOutcomeSelected = useCallback(
        (id: number) => selectedOutcomeIds.has(id),
        [selectedOutcomeIds],
    );
    const isOutcomeSelectedRef = useRef(isOutcomeSelected);
    isOutcomeSelectedRef.current = isOutcomeSelected;
    const stableIsOutcomeSelected = useCallback(
        (id: number) => isOutcomeSelectedRef.current(id),
        [],
    );

    const multipleBetTotalOdds = useMemo(() => {
        const res = betItems.reduce((sum, a) => sum * parseFloat(a.outcomeValue), 1);

        return res === 1 ? 0 : parseFloat(res.toFixed(2));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oddsSignature]);

    const systemBetVariantsCount = useMemo(() => {
        const count: number = betItems.length;

        if (count < 3) {
            return 0;
        }

        return (
            factorial(count) /
            (factorial(systemBetSelection) * factorial(count - systemBetSelection))
        );
    }, [betItems.length, systemBetSelection]);

    const systemBetTotalOdds = useMemo(() => {
        const oddsArray: number[][] = sumOfOddsSubsets(betItems);

        return oddsArray[betItems.length][systemBetSelection];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oddsSignature, systemBetSelection]);

    const totalStake = useMemo(() => {
        if (!betItems.length) return 0;

        return betItems.reduce(
            (memo, item) => (item.stake ? memo + parseFloat(item.stake) : memo + 0),
            0,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stakesSignature]);

    const possibleWinAmount: number = useMemo(() => {
        let win = 0;
        switch (betType) {
            case TicketType.Single:
                for (let i = 0; i < betItems.length; ++i) {
                    win +=
                        parseFloat(betItems[i].outcomeValue) *
                        (betItems[i].stake ? parseFloat(betItems[i].stake) : 0);
                }

                break;
            case TicketType.Express:
                win = multipleBetTotalOdds * (multipleBetStake ? parseFloat(multipleBetStake) : 0);
                break;
            default:
                break;
        }
        return win;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oddsSignature, stakesSignature, betType, multipleBetStake, multipleBetTotalOdds]);

    const getTaxAmount = useCallback(
        (possibleBonusAmount?: number) => {
            let tax = 0;
            if (betProcessorTax?.rate) {
                switch (betType) {
                    case TicketType.Single:
                        tax = ((possibleWinAmount - totalStake) * betProcessorTax.rate) / 100;
                        break;
                    case TicketType.Express: {
                        const bonus = possibleBonusAmount || 0;
                        tax =
                            ((possibleWinAmount -
                                (multipleBetStake ? parseFloat(multipleBetStake) : 0) +
                                bonus) *
                                betProcessorTax.rate) /
                            100;
                        break;
                    }
                    default:
                        break;
                }
            }

            return tax;
        },
        [betProcessorTax?.rate, betType, multipleBetStake, possibleWinAmount, totalStake],
    );

    const getTotalWinAmount = useCallback(
        (possibleBonusAmount?: number) => {
            let win = 0;
            switch (betType) {
                case TicketType.Single:
                    win = possibleWinAmount - getTaxAmount();
                    break;
                case TicketType.Express: {
                    const bonus = possibleBonusAmount || 0;
                    win = possibleWinAmount + bonus;
                    if (betProcessorTax?.enabled) {
                        win -= getTaxAmount(possibleBonusAmount);
                    }
                    break;
                }
                default:
                    break;
            }

            return win;
        },
        [betProcessorTax?.enabled, betType, getTaxAmount, possibleWinAmount],
    );

    const actionsValue = useMemo(
        () => ({
            setBetType,
            setBetMode,
            addBetItem,
            removeBetItem,
            updateBetItem,
            removeAllBetItems,
            updateButtons,
            changeMultipleBetStake,
            changeSystemBetStake,
            changeSystemBetSelection,
            changeBetProcessing: setBetProcessing,
            setBetError: setError,
            isOutcomeSelected: stableIsOutcomeSelected,
        }),
        [
            addBetItem,
            changeMultipleBetStake,
            changeSystemBetSelection,
            changeSystemBetStake,
            removeAllBetItems,
            removeBetItem,
            setBetType,
            updateBetItem,
            updateButtons,
            stableIsOutcomeSelected,
        ],
    );

    const selectionValue = useMemo(
        () => ({
            isOutcomeSelected,
        }),
        [isOutcomeSelected],
    );

    const itemsValue = useMemo(() => ({ betItems }), [betItems]);

    const value = useMemo(
        () => ({
            betType,
            placeBetButtonEnabled,
            betMode,
            multipleBetStake,
            systemBetStake,
            showAcceptButton,
            betProcessing,
            betSlipError: error,
            systemBetSelection,
            multipleBetTotalOdds,
            systemBetVariantsCount,
            systemBetTotalOdds,
            totalStake,
            possibleWinAmount,
            getTaxAmount,
            getTotalWinAmount,
        }),
        [
            betType,
            placeBetButtonEnabled,
            betMode,
            multipleBetStake,
            systemBetStake,
            showAcceptButton,
            betProcessing,
            error,
            systemBetSelection,
            multipleBetTotalOdds,
            systemBetVariantsCount,
            systemBetTotalOdds,
            totalStake,
            possibleWinAmount,
            getTaxAmount,
            getTotalWinAmount,
        ],
    );

    return (
        <BetSlipSelectionContext.Provider value={selectionValue}>
            <BetSlipActionsContext.Provider value={actionsValue}>
                <BetSlipItemsContext.Provider value={itemsValue}>
                    <BetSlipContextV2.Provider value={value}>{children}</BetSlipContextV2.Provider>
                </BetSlipItemsContext.Provider>
            </BetSlipActionsContext.Provider>
        </BetSlipSelectionContext.Provider>
    );
};
